import { isEmpty } from "lodash";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Dimmer, Grid, Loader } from "semantic-ui-react";
import { useGetEnergyType } from "../../../api/common";
import {
  Energy,
  HeatSource,
  RefrigerantSource,
  useCreateFacility,
  useGetFacilityById,
  WaterSource
} from "../../../api/facility";
import {
  useGetCountries,
  useGetHeatSourceType,
  useGetRefrigerantSourceType,
  useGetWaterSourceType
} from "../../../api/static-data";
import { CommonModal } from "../../../components";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import MainBottomMultipleButtonView from "../../../components/mainBottomButtonView/MainBottomMultipleButtonView";
import { FACILITY_OWNERSHIP, FACILITY_TYPES } from "../../../config/constants";
import { errorView } from "../../../helpers/ErrorHandler";
import "../facilityEdit/facilityEdit.scss";
import FacilityEnergySimulation from "../facilitySimulationForms/FacilityEnergySimulation";
import FacilityHeatSourceSimulation from "../facilitySimulationForms/FacilityHeatSourceSimulation";
import FacilityInfoSimulation from "../facilitySimulationForms/FacilityInfoSimulation";
import FacilityRefrigerantSimulation from "../facilitySimulationForms/FacilityRefrigerantSimulation";
import FacilityWaterSourceSimulation from "../facilitySimulationForms/FacilityWaterSourceSimulation";

const FacilitySimulationRegisterModal = ({
  visibleModal,
  setVisibleModal,
  currentFacilityData,
  onSimulationCreated
}: any) => {
  const [visibleDeleteModal, setVisibleDeleteModal] = useState<boolean>(false);
  const [visibleHeatDeleteModal, setVisibleHeatDeleteModal] = useState<boolean>(false);
  const [visibleRefrigerantDeleteModal, setVisibleRefrigerantDeleteModal] =
    useState<boolean>(false);
  const [visibleWaterDeleteModal, setVisibleWaterDeleteModal] = useState<boolean>(false);
  const [deleteData, setDeleteData] = useState<Energy | null>(null);
  const [deleteHeatData, setDeleteHeatData] = useState<HeatSource | null>(null);
  const [deleteRefrigerantData, setDeleteRefrigerantData] = useState<RefrigerantSource | null>(
    null
  );
  const [deleteWaterData, setDeleteWaterData] = useState<WaterSource | null>(null);
  const [defaultFacilityType, setDefaultType] = useState<string>(FACILITY_TYPES[0].value);
  const [defaultFacilityOwnerShip, setDefaultOwnerShip] = useState<string>(
    FACILITY_OWNERSHIP[0].value
  );
  const [facilityData, setFacilityData] = useState(currentFacilityData || {});
  const [country, setCountry] = useState<string>("");

  const { data: energyType, status, fetchStatus } = useGetEnergyType();
  const {
    data: originalFacilityData,
    isFetching: isOriginalFacilityLoading,
    refetch
  } = useGetFacilityById(facilityData?._id);
  const { isLoading, mutate: createFacilitySimulation } = useCreateFacility();

  const deleteFacilitySource = <T,>(
    key: keyof typeof facilityData,
    deleteItem: T | null,
    setDeleteItem: React.Dispatch<React.SetStateAction<T | null>>
  ) => {
    if (!deleteItem) return;

    const list = facilityData?.[key] || [];
    const indexToDelete = list.findIndex((item: any) => item === deleteItem);

    if (indexToDelete !== -1) {
      const updatedList = [...list.slice(0, indexToDelete), ...list.slice(indexToDelete + 1)];

      setFacilityData((prev: any) => ({
        ...prev,
        [key]: updatedList
      }));

      setDeleteItem(null);
    }
  };

  const deleteFacilityEnergy = () => {
    switch (true) {
      case visibleDeleteModal:
        deleteFacilitySource("energy", deleteData, setDeleteData);
        break;
      case visibleHeatDeleteModal:
        deleteFacilitySource("heatSource", deleteHeatData, setDeleteHeatData);
        break;
      case visibleRefrigerantDeleteModal:
        deleteFacilitySource("refrigerantSource", deleteRefrigerantData, setDeleteRefrigerantData);
        break;
      case visibleWaterDeleteModal:
        deleteFacilitySource("waterSource", deleteWaterData, setDeleteWaterData);
        break;
      default:
        break;
    }
  };

  const {
    data: countriesData,
    isSuccess: isCountriesDataSuccess,
    isLoading: isCountriesDataLoading
  } = useGetCountries();

  const {
    data: heatSource,
    status: heatSourceStatus,
    fetchStatus: fetchHeatSource
  } = useGetHeatSourceType();

  const {
    data: waterSource,
    status: waterSourceStatus,
    fetchStatus: fetchWaterSource
  } = useGetWaterSourceType();

  const values = useMemo(() => {
    setDefaultType(facilityData?.facilityType);
    setCountry(facilityData?.facilityCountry);
    setDefaultOwnerShip(facilityData?.facilityOwnership || FACILITY_OWNERSHIP[0].value);
    return {
      facilityName: `${facilityData?.facilityName} - Simulation ${
        originalFacilityData?.simulatedFacilityCount
          ? originalFacilityData?.simulatedFacilityCount + 1
          : 1
      }`,
      facilityAddress: facilityData?.facilityAddress,
      facilityCity: facilityData?.facilityCity,
      facilityCountry: facilityData?.facilityCountry
    };
  }, [facilityData, visibleModal]);

  const countries = useMemo(() => {
    if (isCountriesDataSuccess) {
      return countriesData?.map((c: any) => ({
        key: c.id,
        text: c.country,
        value: c.id
      }));
    }
  }, [countriesData, isCountriesDataSuccess]);

  const {
    register,
    reset,
    formState: { errors },
    getValues,
    setValue: setFacilityValue
  } = useForm({
    defaultValues: values,
    values: values
  });

  const {
    data: refrigerantSource,
    status: refrigerantStatus,
    fetchStatus: refrigerantFetchStatus
  } = useGetRefrigerantSourceType();

  const checkFacilitySaveCondition = (data: any) => {
    if (isEmpty(data.facilityName.trim())) {
      errorView("Facility name is required");
      return true;
    } else if (!country) {
      errorView("Facility country is required");
      return true;
    } else if (facilityData?.energy?.length <= 0) {
      errorView("Please add facility energy");
      return true;
    } else {
      return false;
    }
  };

  const onSubmit = async (data: any) => {
    if (!checkFacilitySaveCondition(data)) {
      const { facilityName, facilityAddress, facilityCity } = data;
      const simulationDetails = {
        facilityName,
        facilityAddress,
        facilityCity,
        facilityCountry: country,
        facilityType: defaultFacilityType,
        facilityOwnership: defaultFacilityOwnerShip,
        energy: facilityData.energy,
        heatSource: facilityData.heatSource,
        waterSource: facilityData.waterSource,
        refrigerantSource: facilityData.refrigerantSource,
        originalId: facilityData?._id
      };
      createFacilitySimulation(
        { data: simulationDetails, simulatedFacility: true },
        {
          onSuccess: () => {
            reset();
            refetch();
            onSimulationCreated?.();
            setVisibleModal(false);
          }
        }
      );
    }
  };

  const saveFacilityData = () => {
    const values = getValues();
    onSubmit(values);
  };

  if (
    (status == "loading" && fetchStatus == "fetching") ||
    (heatSourceStatus == "loading" && fetchHeatSource == "fetching") ||
    (waterSourceStatus == "loading" && fetchWaterSource == "fetching") ||
    (refrigerantStatus == "loading" && refrigerantFetchStatus == "fetching") ||
    isOriginalFacilityLoading ||
    isLoading
  ) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      <CommonModal
        modalOpenState={visibleModal}
        modalCloseState={() => {
          setVisibleModal(false);
        }}
        centered={true}
        title={"Facility Simulation"}
        customDimmer={"custom-dimmer-modal-simulation"}
      >
        <Grid>
          <Grid.Column computer={16} tablet={16} mobile={16}>
            <CommonSplitContainer
              currentHeight={0.91}
              topChild={
                <>
                  <FacilityInfoSimulation
                    register={register}
                    errors={errors}
                    country={country}
                    countries={countries}
                    setFacilityValue={setFacilityValue}
                    setCountry={setCountry}
                    isCountriesDataLoading={isCountriesDataLoading}
                    FACILITY_TYPES={FACILITY_TYPES}
                    defaultFacilityType={defaultFacilityType}
                    setDefaultType={setDefaultType}
                    defaultFacilityOwnerShip={defaultFacilityOwnerShip}
                    setDefaultOwnerShip={setDefaultOwnerShip}
                    facilityData={facilityData}
                    originalFacilityData={originalFacilityData}
                    isFacilitySimulation={true}
                  />
                  <FacilityEnergySimulation
                    energyType={energyType}
                    currentFacilityData={facilityData?.energy || []}
                    originalFacilityEnergyData={originalFacilityData?.energy || []}
                    setDeleteData={setDeleteData}
                    setVisibleDeleteModal={setVisibleDeleteModal}
                    isFacilitySimulation={true}
                  />
                  <FacilityHeatSourceSimulation
                    heatType={heatSource}
                    currentFacilityHeatData={facilityData?.heatSource || []}
                    originalHeatSourceData={originalFacilityData?.heatSource || []}
                    setDeleteHeatData={setDeleteHeatData}
                    setVisibleDeleteHeatModal={setVisibleHeatDeleteModal}
                    isFacilitySimulation={true}
                  />
                  <FacilityWaterSourceSimulation
                    waterType={waterSource}
                    currentFacilityWaterData={facilityData?.waterSource || []}
                    originalWaterSourceData={originalFacilityData?.waterSource || []}
                    setDeleteWaterData={setDeleteWaterData}
                    setVisibleDeleteWaterModal={setVisibleWaterDeleteModal}
                    isFacilitySimulation={true}
                  />
                  <FacilityRefrigerantSimulation
                    refrigerantType={refrigerantSource}
                    currentFacilityRefrigerantData={facilityData?.refrigerantSource || []}
                    originalRefrigerantSourceData={originalFacilityData?.refrigerantSource || []}
                    setDeleteRefrigerantData={setDeleteRefrigerantData}
                    setVisibleDeleteRefrigerantModal={setVisibleRefrigerantDeleteModal}
                    isFacilitySimulation={true}
                  />
                </>
              }
              bottomChild={
                <MainBottomMultipleButtonView
                  leftButtons={[
                    {
                      title: "Cancel",
                      onClick: () => {
                        setVisibleModal(false);
                      },
                      buttonOutLine: true,
                      buttonView: true
                    }
                  ]}
                  rightButtons={[
                    {
                      contentView: 16,
                      title: "Save Simulation",
                      onClick: () => saveFacilityData(),
                      type: "submit",
                      buttonView: true
                    }
                  ]}
                />
              }
            />
          </Grid.Column>
        </Grid>
      </CommonModal>
      <ConfirmModal
        viewModal={
          visibleDeleteModal ||
          visibleHeatDeleteModal ||
          visibleWaterDeleteModal ||
          visibleRefrigerantDeleteModal
        }
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => {
          setVisibleDeleteModal(false);
          setDeleteData(null);
          setVisibleHeatDeleteModal(false);
          setDeleteHeatData(null);
          setVisibleWaterDeleteModal(false);
          setDeleteWaterData(null);
          setVisibleRefrigerantDeleteModal(false);
          setDeleteRefrigerantData(null);
        }}
        approve={() => {
          setVisibleDeleteModal(false);
          setVisibleHeatDeleteModal(false);
          setVisibleWaterDeleteModal(false);
          setVisibleRefrigerantDeleteModal(false);
          deleteFacilityEnergy();
        }}
        title="Delete Facility Energy"
        subTitle="Are you sure you want to delete facility energy?"
      />
    </>
  );
};

export default FacilitySimulationRegisterModal;
